// Medical Portal — Query detail, read from Trust OPM
//
// Opened from Report Queries (and the dashboard's complaints tab). The
// list record renders at once; the detail fetch fills the rest. Shows
// the same lifecycle the CSD sees in OPM: received → with department →
// in progress → resolved / closed, plus the department, the ticket and
// the corrective action Quality reported. The preventive action stays
// in QTQMS and is never shown here.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { MP, MONO, DISPLAY, StatusBadge, Pill } from './shared';
import {
  useGetPortalComplaintQuery,
  useReplyPortalComplaintMutation,
} from '../../../store/queriesApi';

// The questions thread is the tallest thing in this dialog — three answered
// questions already push the corrective action off the screen. Collapsed, it
// is clipped to this height with a fade and a "view full conversation"
// control.
//
// Expanded, it is NOT given its own scrollbar. The dialog is scroll="paper",
// so DialogContent is already the scroller; a second scroller inside it means
// the wheel only moves the thread, and once the thread fills the dialog there
// is nowhere left to put the pointer to move the dialog. Expanding simply
// removes the cap and lets the one scroller the dialog already has do its job.
const Q_COLLAPSED = 380;

const STEPS = [
  { key: 'received',   label: 'Received' },
  { key: 'department', label: 'With department' },
  { key: 'progress',   label: 'In progress' },
  { key: 'resolved',   label: 'Resolved & Closed' },
];

export const prettyDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(String(iso).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const Stepper = ({ stage }) => (
  <Stack direction="row" alignItems="center" justifyContent="center" sx={{ overflowX: 'auto', pb: 1 }}>
    {STEPS.map((s, i) => {
      const done = i <= stage;
      const current = i === stage;
      return (
        <Stack key={s.key} direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
          <Stack alignItems="center" sx={{ minWidth: 96 }} spacing={0.75}>
            <Box sx={{
              width: 32, height: 32, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: done ? MP.teal : MP.bg,
              color: done ? '#fff' : MP.textMuted,
              border: `2px solid ${current ? MP.tealDark : done ? MP.teal : MP.border}`,
              fontSize: 13, fontWeight: 700,
              boxShadow: current ? `0 0 0 4px ${MP.tealLight}` : 'none',
            }}>
              {done ? '✓' : i + 1}
            </Box>
            <Typography sx={{
              fontSize: 10.5, fontFamily: MONO, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              color: done ? MP.tealDark : MP.textMuted, textAlign: 'center',
            }}>
              {s.label}
            </Typography>
          </Stack>
          {i < STEPS.length - 1 && (
            <Box sx={{ width: 36, height: 2, bgcolor: i < stage ? MP.teal : MP.border, mb: 3 }} />
          )}
        </Stack>
      );
    })}
  </Stack>
);

const Field = ({ label, value, mono = false }) => (
  <Box>
    <Typography sx={{ fontSize: 10.5, fontFamily: MONO, fontWeight: 700, letterSpacing: '0.08em', color: MP.textMuted, textTransform: 'uppercase' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 13, color: MP.dark, fontWeight: 600, fontFamily: mono ? MONO : undefined, wordBreak: 'break-word' }}>
      {value || '—'}
    </Typography>
  </Box>
);

const fileIcon = (name = '') => {
  const n = String(name).toLowerCase();
  if (/\.(png|jpe?g|webp|gif|heic|heif)$/.test(n)) return <ImageOutlinedIcon sx={{ fontSize: 22, color: MP.teal }} />;
  if (n.endsWith('.pdf')) return <PictureAsPdfOutlinedIcon sx={{ fontSize: 22, color: '#D93025' }} />;
  return <DescriptionOutlinedIcon sx={{ fontSize: 22, color: MP.textMuted }} />;
};

const ComplaintDetailModal = ({ open, onClose, record: listRecord }) => {
  const id = listRecord?.id;
  const { data, isFetching, refetch } = useGetPortalComplaintQuery({ id }, { skip: !open || !id });
  const record = useMemo(
    () => (data?.record ? { ...listRecord, ...data.record } : listRecord),
    [listRecord, data],
  );

  // Answering a question the desk forwarded from the department. This is
  // the only message a portal user writes; there is no open conversation.
  const [answer, setAnswer] = useState('');
  const [answerErr, setAnswerErr] = useState('');
  const [sendAnswer, { isLoading: sending }] = useReplyPortalComplaintMutation();
  const submitAnswer = async () => {
    const message = answer.trim();
    if (!message) return;
    setAnswerErr('');
    try {
      await sendAnswer({ id, message }).unwrap();
      setAnswer('');
      refetch();
    } catch (err) {
      setAnswerErr(err?.data?.message || 'Could not send your answer.');
    }
  };

  // Questions from the lab with the answers given. Older records forwarded
  // before this list existed still show their open question. Derived above
  // the `!record` guard so the thread's hooks below never sit after an
  // early return.
  const questions = useMemo(() => {
    if (!record) return [];
    if (record.questions && record.questions.length) return record.questions;
    if (record.info_request && record.status !== 'closed' && !record.info_request.answered) {
      return [{
        id: 'ask', at: record.info_request.at, text: record.info_request.text,
        answers: [], answered: false, open: true,
      }];
    }
    return [];
  }, [record]);
  const openQ = questions.some((q) => q.open);

  // Thread window. `qOverflows` decides whether the toggle is worth showing
  // at all — a one-question thread that already fits gets no control.
  const [qExpanded, setQExpanded] = useState(false);
  const [qOverflows, setQOverflows] = useState(false);
  const qListRef = useRef(null);
  const qCardRef = useRef(null);

  const toggleQ = () => {
    const next = !qExpanded;
    setQExpanded(next);
    // Collapsing removes hundreds of pixels above the fold, which would
    // otherwise leave the dialog scrolled somewhere past this card.
    if (!next) {
      requestAnimationFrame(() => {
        qCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }
  };

  // `record` is rebuilt on most renders, so `questions` is a new array each
  // time. Keying the measure on the thread's shape instead means reading
  // scrollHeight (a layout flush) only when the content actually changed —
  // a new question, or an answer added to one.
  const qSig = questions.reduce((n, q) => n + 1 + (q.answers?.length || 0), 0);

  useLayoutEffect(() => {
    const el = qListRef.current;
    if (!el) { setQOverflows(false); return; }
    // scrollHeight is the full content height in both states, so this
    // measures the same thing whether the window is open or clipped.
    setQOverflows(el.scrollHeight > Q_COLLAPSED + 16);
  }, [qSig, open, isFetching]);

  // A new query opens clipped …
  useEffect(() => { setQExpanded(false); }, [id]);
  // … unless a question is still waiting on an answer. The reply box lives
  // at the bottom of that question, so clipping would bury the one thing
  // the user came here to do. While one is open the thread stays open and
  // the toggle is withheld (it scrolls instead).
  useEffect(() => { if (openQ) setQExpanded(true); }, [openQ, id]);

  if (!record) return null;
  const isPatient = !!record.visit_id;
  const ca = record.corrective_action;
  const showQToggle = qOverflows && !openQ;
  const qWindowed = qExpanded || openQ;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { borderRadius: '6px', bgcolor: MP.white, maxHeight: '92vh' } }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2.25, borderBottom: `1px solid ${MP.border}` }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontFamily: DISPLAY, fontSize: 20, fontWeight: 700, letterSpacing: '-.01em', color: MP.dark, lineHeight: 1.2 }}>
              {`QUERY · ${record.no}`}
            </Typography>
            <StatusBadge>{record.status_label}</StatusBadge>
            <Pill tone={isPatient ? 'teal' : 'yellow'}>{isPatient ? 'Patient-specific' : 'General service'}</Pill>
            {record.department && <Pill tone="purple">{record.department}</Pill>}
          </Stack>
          <Typography sx={{ fontSize: 12, color: MP.textMuted, mt: 0.5, fontFamily: MONO }}>
            {record.query_code && <Box component="span" sx={{ color: MP.tealDark, fontWeight: 700 }}>{record.query_code}{' · '}</Box>}
            {'Raised on '}{prettyDateTime(record.raised_at)}
            {record.raised_by_user?.name && (
              <> {' · By '}<Box component="span" sx={{ color: MP.tealDark, fontWeight: 700 }}>{record.raised_by_user.name}</Box>{' ('}{record.raised_by_user.role}{')'}</>
            )}
            {isFetching && !data && <> {' · '}<Box component="span" sx={{ color: MP.teal }}>loading…</Box></>}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ flexShrink: 0 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {/* Lifecycle, as OPM tracks it */}
        <Box sx={{ border: `1px solid ${MP.border}`, borderRadius: '6px', px: 2, pt: 2, pb: 1, mb: 2.5, bgcolor: MP.white }}>
          <Stepper stage={record.stage ?? 0} />
          <Typography sx={{ fontSize: 12, color: MP.textMuted, textAlign: 'center', mt: 0.5 }}>
            {record.status === 'open' && 'Received by the Customer Service Desk, awaiting assignment to a department.'}
            {record.status === 'assigned' && `Assigned to ${record.department || 'a department'}${record.assigned_at ? ` on ${prettyDateTime(record.assigned_at)}` : ''}.`}
            {record.status === 'in_progress' && `${record.department || 'The department'} is working on it.`}
            {record.status === 'resolved' && `Resolved${record.resolved_at ? ` on ${prettyDateTime(record.resolved_at)}` : ''}.`}
            {record.status === 'closed' && `Closed${record.closed_at ? ` on ${prettyDateTime(record.closed_at)}` : ''}.`}
          </Typography>
        </Box>

        {/* Talk to a TrustLab doctor — asked, and whether the call was made. */}
        {record.callback && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, px: 2.25, py: 1.5, borderRadius: '6px',
            border: `1px solid ${record.callback.called ? MP.teal : MP.blue}33`,
            bgcolor: record.callback.called ? MP.tealLight : MP.blueLight,
          }}>
            <Box sx={{ fontSize: 20, lineHeight: 1 }}>📞</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: MP.dark }}>
                {record.callback.called ? 'A TrustLab doctor called you' : 'A TrustLab doctor will call you shortly'}
              </Typography>
              <Typography sx={{ fontSize: 12, color: MP.textMuted, fontFamily: MONO }}>
                {record.callback.called
                  ? `${record.callback.called_by ? `${record.callback.called_by} · ` : ''}${prettyDateTime(record.callback.called_at)}`
                  : `On ${record.callback.phone} · asked ${prettyDateTime(record.callback.at)}`}
              </Typography>
            </Box>
            <Pill tone={record.callback.called ? 'green' : 'blue'}>{record.callback.called ? 'Called' : 'Callback requested'}</Pill>
          </Box>
        )}

        {/* Details */}
        <Box sx={{ border: `1px solid ${MP.border}`, borderRadius: '6px', px: 2.25, py: 2, mb: 2.5, bgcolor: MP.white, boxShadow: MP.shadow }}>
          <Grid container spacing={2}>
            {isPatient && <Grid item xs={6} md={3}><Field label="Visit" value={record.visit_id} mono /></Grid>}
            {record.uhid && <Grid item xs={6} md={3}><Field label="UHID" value={record.uhid} mono /></Grid>}
            <Grid item xs={6} md={3}><Field label="Category" value={record.category_label} /></Grid>
            {record.subcategory && <Grid item xs={6} md={3}><Field label="Sub-category" value={record.subcategory} /></Grid>}
            {record.branch && <Grid item xs={6} md={3}><Field label="Branch" value={record.branch} /></Grid>}
            {record.lab && <Grid item xs={6} md={3}><Field label="Lab" value={record.lab} /></Grid>}
            <Grid item xs={6} md={3}><Field label="Priority" value={record.priority} /></Grid>
            <Grid item xs={6} md={3}><Field label="Department" value={record.department} /></Grid>
            {record.ticket_code && (
              <Grid item xs={6} md={3}>
                <Field label="Ticket" value={`${record.ticket_code}${record.ticket_status ? ` · ${record.ticket_status}` : ''}`} mono />
              </Grid>
            )}
            {record.qtqms_ticket && (
              <Grid item xs={6} md={3}>
                <Field label="Quality ticket" value={`${record.qtqms_ticket}${record.qtqms_status ? ` · ${record.qtqms_status}` : ''}`} mono />
              </Grid>
            )}
            {record.detector && <Grid item xs={6} md={3}><Field label="Reported by" value={record.detector} /></Grid>}
            {record.last_reply_at && <Grid item xs={6} md={3}><Field label="Last reply" value={prettyDateTime(record.last_reply_at)} /></Grid>}
          </Grid>
        </Box>

        {/* Description */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, fontFamily: MONO, letterSpacing: '0.1em', color: MP.textMuted, mb: 0.75 }}>
            QUERY
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: MP.text, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
            {record.description || record.subject || '(No description)'}
          </Typography>
        </Box>

        {/* Attachments */}
        {(record.attachments || []).length > 0 && (
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, fontFamily: MONO, letterSpacing: '0.1em', color: MP.textMuted, mb: 0.75 }}>
              ATTACHMENTS
            </Typography>
            <Stack spacing={1}>
              {record.attachments.map((f, i) => (
                <Box
                  key={`${f.url || f.name}-${i}`}
                  component="a" href={f.url || '#'} target="_blank" rel="noopener noreferrer"
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.25, textDecoration: 'none',
                    border: `1px solid ${MP.border}`, borderRadius: '6px', px: 1.5, py: 1,
                    '&:hover': { borderColor: MP.teal },
                  }}
                >
                  {fileIcon(f.name)}
                  <Typography sx={{ fontSize: 13, color: MP.dark, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {f.name}
                  </Typography>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: MP.tealDark, whiteSpace: 'nowrap' }}>View →</Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {/* Questions from the lab — each question the department asked
            (forwarded by the Customer Service Desk) with the answers given.
            The answer box appears only under a question still waiting. */}
        {questions.length > 0 && (
          <Box ref={qCardRef} sx={{ border: `1px solid ${MP.border}`, borderRadius: '6px', mb: 2.5, bgcolor: MP.white, boxShadow: MP.shadow, overflow: 'hidden', scrollMarginTop: '12px' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between"
              sx={{
                px: 2.25, py: 1.5, borderBottom: `1px solid ${MP.border}`,
                bgcolor: openQ ? MP.amberLight : MP.bg,
              }}>
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, fontFamily: MONO, letterSpacing: '0.1em', color: openQ ? MP.amber : MP.textMuted }}>
                  QUESTIONS FROM THE LAB · {questions.length}
                </Typography>
                {/* Only meaningful once the thread is long enough to clip. */}
                {showQToggle && !qExpanded && (
                  <Typography sx={{ fontSize: 10.5, color: MP.ink3, fontFamily: MONO }}>
                    showing part
                  </Typography>
                )}
              </Stack>
              {openQ
                ? <Pill tone="amber">Reply needed</Pill>
                : <Pill tone="green">All answered</Pill>}
            </Stack>

            {/* Clipped at Q_COLLAPSED until opened; opening removes the cap
                and the dialog's own scroller takes over. No nested scroller. */}
            <Box sx={{ position: 'relative' }}>
              <Stack
                ref={qListRef}
                divider={<Box sx={{ borderTop: `1px dashed ${MP.border}` }} />}
                sx={{
                  maxHeight: qWindowed ? 'none' : `${Q_COLLAPSED}px`,
                  overflow: qWindowed ? 'visible' : 'hidden',
                }}
              >
                {questions.map((q, i) => (
                  <Box key={q.id || i} sx={{ px: 2.25, py: 2 }}>
                    {/* the question */}
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Box sx={{
                        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: q.answered ? MP.tealLight : MP.amberLight,
                        color: q.answered ? MP.tealDark : MP.amber,
                        fontFamily: MONO, fontSize: 11, fontWeight: 700,
                      }}>
                        Q{i + 1}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 700, color: MP.dark }}>Customer Service Desk</Typography>
                          <Typography sx={{ fontFamily: MONO, fontSize: 10.5, color: MP.textMuted }}>{prettyDateTime(q.at)}</Typography>
                          {q.answered
                            ? <Pill tone="green">Answered</Pill>
                            : q.open ? <Pill tone="amber">Awaiting your answer</Pill> : <Pill tone="grey">No answer</Pill>}
                        </Stack>
                        <Box sx={{
                          bgcolor: MP.bg, border: `1px solid ${MP.border}`, borderLeft: `3px solid ${q.answered ? MP.teal : MP.amber}`,
                          borderRadius: '6px', px: 1.75, py: 1.25,
                        }}>
                          <Typography sx={{ fontSize: 13, color: MP.text, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{q.text}</Typography>
                        </Box>

                        {/* the answers */}
                        {q.answers.map((a) => (
                          <Box key={a.id || a.at} sx={{ mt: 1.25, ml: { xs: 1.5, sm: 4 } }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                              <Typography sx={{ fontSize: 12, fontWeight: 700, color: MP.tealDark }}>
                                {a.by ? `Your answer · ${a.by}` : 'Your answer'}
                              </Typography>
                              <Typography sx={{ fontFamily: MONO, fontSize: 10.5, color: MP.textMuted }}>{prettyDateTime(a.at)}</Typography>
                            </Stack>
                            <Box sx={{ bgcolor: MP.tealLight, border: `1px solid ${MP.teal}33`, borderRadius: '6px', px: 1.75, py: 1.25 }}>
                              <Typography sx={{ fontSize: 13, color: MP.text, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{a.text}</Typography>
                            </Box>
                          </Box>
                        ))}

                        {/* the answer box — only under the question still waiting */}
                        {q.open && (
                          <Box sx={{ mt: 1.5, ml: { xs: 1.5, sm: 4 } }}>
                            <TextField
                              size="small" fullWidth multiline minRows={2}
                              placeholder="Type your answer…"
                              value={answer}
                              onChange={(e) => setAnswer(e.target.value)}
                              error={!!answerErr}
                              helperText={answerErr || 'Goes to the Customer Service Desk, who pass it to the department.'}
                              sx={{ bgcolor: MP.white, borderRadius: '6px' }}
                            />
                            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.5 }}>
                              <Button
                                variant="contained" size="small" disabled={sending || !answer.trim()}
                                onClick={submitAnswer}
                                sx={{ textTransform: 'none', fontWeight: 600, bgcolor: MP.teal, '&:hover': { bgcolor: MP.tealDark } }}
                              >
                                {sending ? 'Sending…' : 'Send answer'}
                              </Button>
                            </Stack>
                          </Box>
                        )}
                      </Box>
                    </Stack>
                  </Box>
                ))}
              </Stack>

              {/* Fade over the cut edge, so a clipped thread reads as
                  continuing rather than as ending mid-sentence. Never
                  intercepts clicks. */}
              {showQToggle && !qExpanded && (
                <Box sx={{
                  position: 'absolute', left: 0, right: 0, bottom: 0, height: 72,
                  pointerEvents: 'none',
                  background: `linear-gradient(to bottom, rgba(255,255,255,0) 0%, ${MP.white} 85%)`,
                }} />
              )}
            </Box>

            {showQToggle && (
              <Stack
                direction="row" alignItems="center" justifyContent="center"
                sx={{ borderTop: `1px solid ${MP.borderSoft}`, bgcolor: MP.bg }}
              >
                <Button
                  fullWidth
                  disableRipple
                  onClick={toggleQ}
                  endIcon={(
                    <ExpandMoreIcon sx={{
                      fontSize: 18,
                      transform: qExpanded ? 'rotate(180deg)' : 'none',
                      transition: 'transform .18s ease',
                    }} />
                  )}
                  sx={{
                    textTransform: 'none', fontWeight: 600, fontSize: 12.5,
                    color: MP.tealDark, py: 1, borderRadius: 0,
                    '&:hover': { bgcolor: MP.tealLight },
                  }}
                >
                  {qExpanded
                    ? 'Show less'
                    : `View full conversation · ${questions.length} question${questions.length === 1 ? '' : 's'}`}
                </Button>
              </Stack>
            )}
          </Box>
        )}

        {/* Corrective action — what the lab did about it, reported by
            Quality once the ticket is resolved. A summary, not a chat. */}
        <Box sx={{ border: `1px solid ${MP.border}`, borderRadius: '6px', px: 2.25, py: 2, bgcolor: ca ? MP.greenLight : MP.white, boxShadow: MP.shadow }}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, fontFamily: MONO, letterSpacing: '0.1em', color: ca ? MP.green : MP.textMuted, mb: 1 }}>
            CORRECTIVE ACTION{ca?.at ? ` · ${prettyDateTime(ca.at)}` : ''}
          </Typography>
          {!ca ? (
            <Typography sx={{ fontSize: 12.5, color: MP.textMuted }}>
              {record.status === 'open'
                ? 'Received by the Customer Service Desk, awaiting assignment to a department.'
                : record.status === 'closed' || record.status === 'resolved'
                  ? 'Resolved. No corrective action summary was recorded.'
                  : `With ${record.department || 'the department'}. The corrective action will appear here once the query is resolved.`}
            </Typography>
          ) : (
            <Box>
              {(ca.actions || []).length > 0 && (
                <Box component="ul" sx={{ m: 0, mb: ca.summary ? 1 : 0, pl: 2.5 }}>
                  {ca.actions.map((a, i) => (
                    <Typography key={i} component="li" sx={{ fontSize: 13.5, color: MP.text, lineHeight: 1.6 }}>{a}</Typography>
                  ))}
                </Box>
              )}
              {ca.amended_report && (
                <Typography sx={{ fontSize: 12.5, color: MP.textMuted, mb: 0.75 }}>
                  Amended report: <Box component="b" sx={{ fontFamily: MONO, color: MP.dark }}>{ca.amended_report}</Box>
                </Typography>
              )}
              {ca.summary && (
                <Typography sx={{ fontSize: 13.5, color: MP.text, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
                  {ca.summary}
                </Typography>
              )}
              {ca.verified_by && (
                <Typography sx={{ fontSize: 12, color: MP.textMuted, mt: 1 }}>
                  Verified by <Box component="b" sx={{ color: MP.dark }}>{ca.verified_by}</Box>{ca.date ? ` · ${ca.date}` : ''}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 3, py: 2, borderTop: `1px solid ${MP.border}` }}>
        <Button variant="outlined" size="small" onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

export default ComplaintDetailModal;
